import React, { useState, useEffect } from "react";
import { Router, Route, Redirect, Switch, useHistory } from "react-router-dom";
import { browserHistory } from "./helpers/history";
import StandardPageComp from "./components/standardPage";
import FullWidthPageComp from "./components/fullWidthPage";
import B2CAuthHoc from "./components/b2cAuthHoc";
import Home from "./components/home";
import FinancialsLandingPage from "./components/financials";
import ResourcesLandingPage from "./components/resources";
import AccountSettings from "./components/accountSettings";
import Login from "./components/login";
import AdReply from "./components/adReply";
import Report from "./components/report";
import GreatBearReport from "./components/greatBearReport";
import RegattaReport from "./components/regattaReport";
import PowerBiReports from "./components/powerBiReports";
import Timesheets from "./components/timesheets/timesheets";
import TimesheetApprove from "./components/timesheets/TimesheetWithinEmail/timesheetApprove";
import TimesheetReject from "./components/timesheets/TimesheetWithinEmail/timesheetReject";
import LoadingMessage from "./components/loadingMessage";
import Bookings from "./components/bookings/bookings";
import Documentation from "./components/documentation/ReflexAllen/documentation";
import References from "./components/references/references";
import TeachrightReferences from "./components/teachrightReferences/references";
import isPublicRoute from "./helpers/publicRoutes";
import DocumentationGB from "./components/documentation/GreatBear/documentationGB";
import DocumentationK from "./components/documentation/Kingspan/documentationK";
import DocumentationPUS from "./components/documentation/Pusterla/documentationPUS";
import DocumentationREG from "./components/documentation/Regatta/documentationREG";
import DocumentationSigma from "./components/documentation/Sigma/documentationSigma";
import DocumentationIpn from "./components/documentation/Ipn/documentationIpn";
import DocumentationUps from "./components/documentation/Ups/documentationUps";
import "./App.css";
import DocumentationPEC from "./components/documentation/PecanDeluxe/documentationPEC";
import DocumentationSAS from "./components/documentation/SeniorArcSystems/documentationSAS";
import DocumentationCF from "./components/documentation/CarltonForest/documentationCF";
import DocumentationCPI from "./components/documentation/CPI/documentationCPI";
import DocumentationTDC from "./components/documentation/ThanetDistrictCouncil/documentationTDC";
import DocumentationMolecular from "./components/documentation/Molecular/documentationMolecular";
import DocumentationCompleatFood from "./components/documentation/CompleatFood/documentationCompleatFood";
import DocumentationWood from "./components/documentation/Woodland/documentationWood";
import DocumentationGrowUpFarms from "./components/documentation/GrowUpFarms/documentationGrowUpFarms";
import DocumentationDF from "./components/documentation/DavinFoods/documentationDF";

const RouterBase = ({ children }) => (
  <main>
    <Router history={browserHistory}>
      <div>{children}</div>
    </Router>
  </main>
);

const ProtectedRoute = props => {
  const history = useHistory();
  const [enabledRouting, setEnabledRouting] = useState(false);

  useEffect(() => {
    if (props.allowed && enabledRouting) {
      history.push(props.path);
    }
  }, [props.watch]);

  return (
    <Route
      {...props}
      render={() => {
        setEnabledRouting(true);
        if (props.allowed === undefined) {
          return <LoadingMessage height={"200px"} absolutePosition={false} />;
        }
        return props.allowed ? props.render() : <Redirect to="/login" />;
      }}
    />
  );
};

const App = ({
  checkingLogin,
  tokenStore,
  handleLogin,
  companyRef,
  accountPermission
}) => {
  const withLoginState = WrappedComponent => props => (
    <WrappedComponent
      loggedIn={tokenStore.isLoggedIn}
      user={tokenStore.getUser()}
      accountPermission={accountPermission}
      {...props}
    />
  );
  const StandardPage = withLoginState(StandardPageComp);
  const FullWidthPage = withLoginState(FullWidthPageComp);

  if (isPublicRoute()) {
    return (
      <RouterBase>
        <Route
          exact
          path="/references/:token"
          render={props => (
            <StandardPage>
              <References {...props} />
            </StandardPage>
          )}
        />
        <Route
          exact
          path="/education-references/:token"
          render={props => (
            <StandardPage>
              <TeachrightReferences {...props} />
            </StandardPage>
          )}
        />
      </RouterBase>
    );
  }

  if (checkingLogin) {
    return (
      <RouterBase>
        <Route
          path="*"
          render={() => (
            <StandardPage>
              <LoadingMessage height={"200px"} absolutePosition={false} />
            </StandardPage>
          )}
        />
      </RouterBase>
    );
  }

  if (!tokenStore.isLoggedIn()) {
    const url = window.location.pathname.replace("/account-portal", "");
    if (url !== "/settings") {
      sessionStorage.setItem("last-url", url);
    }
    return (
      <RouterBase>
        <Switch>
          <Route
            exact
            path="/login"
            render={() => (
              <FullWidthPage>
                <Login handleLogin={handleLogin} />
              </FullWidthPage>
            )}
          />
          <Route exact path="/callback(state.*)" component={AdReply} />
          <Route path="*" render={() => <Redirect to="/login" />} />
        </Switch>
      </RouterBase>
    );
  }

  return (
    <RouterBase>
      <Route
        exact
        path="/login"
        render={() => {
          const lastUrl = sessionStorage.getItem("last-url");
          sessionStorage.removeItem("last-url");
          return <Redirect to={lastUrl || "/"} />;
        }}
      />
      <Route
        exact
        path="/"
        render={() => (
          <StandardPage>
            <Home />
          </StandardPage>
        )}
      />
      <Route
        exact
        path="/financials"
        render={() => (
          <StandardPage>
            <FinancialsLandingPage />
          </StandardPage>
        )}
      />
      <Route
        exact
        path="/resources"
        render={() => (
          <StandardPage>
            <ResourcesLandingPage accountPermission={accountPermission} />
          </StandardPage>
        )}
      />
      <Route
        path="/credit-summary"
        render={() => (
          <StandardPage>
            <Report
              tokenStore={tokenStore}
              companyRef={companyRef}
              pageId={process.env.REACT_APP_CREDIT_SUMMARY_PAGE_ID}
            />
          </StandardPage>
        )}
      />
      <Route
        path="/worker-summary"
        render={() => (
          <StandardPage>
            <Report
              tokenStore={tokenStore}
              companyRef={companyRef}
              pageId={process.env.REACT_APP_WORKER_SUMMARY_PAGE_ID}
            />
          </StandardPage>
        )}
      />
      <Route
        path="/ipn-report"
        render={() => (
          <StandardPage>
            <PowerBiReports
              reportId="8c069954-7f30-46e0-b55e-b8c2ba58d52a"
              tokenStore={tokenStore}
            />
          </StandardPage>
        )}
      />
      <Route
        path="/ipn-induction"
        render={() => (
          <StandardPage>
            <DocumentationIpn tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <Route
        path="/aldi-report"
        render={() => (
          <StandardPage>
            <PowerBiReports
              reportId="d5fbbd89-f28d-4137-b45b-209c945950ed"
              tokenStore={tokenStore}
            />
          </StandardPage>
        )}
      />
      <Route
        path="/great-bear-report"
        render={() => (
          <StandardPage>
            <GreatBearReport tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <Route
        path="/speciality-breads-total-hours-report"
        render={() => (
          <StandardPage>
            <PowerBiReports
              reportId="d0d960ec-97c4-438f-9277-6092d1bf2aca"
              tokenStore={tokenStore}
              pageId="ReportSectiond54d9e303c5a63268108"
            />
          </StandardPage>
        )}
      />
      <Route
        path="/speciality-breads-absences-report"
        render={() => (
          <StandardPage>
            <PowerBiReports
              reportId="d0d960ec-97c4-438f-9277-6092d1bf2aca"
              tokenStore={tokenStore}
              pageId="ReportSection1a26a05fc3202dc761d5"
            />
          </StandardPage>
        )}
      />
      <Route
        path="/regatta-starters"
        render={() => (
          <StandardPage>
            <RegattaReport
              tokenStore={tokenStore}
              pageId="ReportSectiona3462a15827434f01a4d"
            />
          </StandardPage>
        )}
      />
      <Route
        path="/regatta-leavers"
        render={() => (
          <StandardPage>
            <RegattaReport tokenStore={tokenStore} pageId="ReportSection" />
          </StandardPage>
        )}
      />
      <Route
        path="/regatta-absenses"
        render={() => (
          <StandardPage>
            <RegattaReport
              tokenStore={tokenStore}
              pageId="ReportSectionf7154ba5e15392c28179"
            />
          </StandardPage>
        )}
      />
      <Route
        path="/regatta-attrition"
        render={() => (
          <StandardPage>
            <RegattaReport
              tokenStore={tokenStore}
              pageId="ReportSectiond627ebfcea8441326591"
            />
          </StandardPage>
        )}
      />
      <Route
        path="/settings"
        render={() => (
          <StandardPage>
            <AccountSettings tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <Route
        path="/timesheets"
        render={() => (
          <StandardPage>
            <Timesheets companyRef={companyRef} tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <Route
        path="/timesheet-approve/:timesheetRef"
        render={() => (
          <StandardPage>
            <TimesheetApprove tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <Route
        path="/timesheet-reject/:timesheetRef"
        render={() => (
          <StandardPage>
            <TimesheetReject tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <Route
        path="/vacancies"
        render={() => (
          <StandardPage>
            <Bookings tokenStore={tokenStore} itemsPerPage={6} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isKingspan}
        path="/documentationK"
        render={() => (
          <StandardPage>
            <DocumentationK tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isPusterla}
        path="/documentationPUS"
        render={() => (
          <StandardPage>
            <DocumentationPUS tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isRegatta}
        path="/documentationREG"
        render={() => (
          <StandardPage>
            <DocumentationREG tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isPusterla}
        path="/documentationSigma"
        render={() => (
          <StandardPage>
            <DocumentationSigma tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isUps}
        path="/documentationUps"
        render={() => (
          <StandardPage>
            <DocumentationUps tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isGreatBear}
        path="/documentationGB"
        render={() => (
          <StandardPage>
            <DocumentationGB tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isPecan}
        path="/documentationPEC"
        render={() => (
          <StandardPage>
            <DocumentationPEC tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isWoodland}
        path="/documentationWoodland"
        render={() => (
          <StandardPage>
            <DocumentationWood tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.seeReflexAndAllenDocuments}
        path="/documentation"
        render={() => (
          <StandardPage>
            <Documentation tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isSeniorArcSystems}
        path="/documentationSAS"
        render={() => (
          <StandardPage>
            <DocumentationSAS tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isCPI}
        path="/documentationCPI"
        render={() => (
          <StandardPage>
            <DocumentationCPI tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isCarltonForest}
        path="/documentationCF"
        render={() => (
          <StandardPage>
            <DocumentationCF tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isThanetDistrictCouncil}
        path="/documentationTDC"
        render={() => (
          <StandardPage>
            <DocumentationTDC tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isMolecular}
        path="/documentationMolecular"
        render={() => (
          <StandardPage>
            <DocumentationMolecular tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isCompleatFood}
        path="/documentationCompleatFood"
        render={() => (
          <StandardPage>
            <DocumentationCompleatFood tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isGrowUpFarms}
        path="/documentationGrowUpFarms"
        render={() => (
          <StandardPage>
            <DocumentationGrowUpFarms tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
      <ProtectedRoute
        watch={accountPermission}
        allowed={accountPermission.isDavinFoods}
        path="/documentationDF"
        render={() => (
          <StandardPage>
            <DocumentationDF tokenStore={tokenStore} />
          </StandardPage>
        )}
      />
    </RouterBase>
  );
};

export default B2CAuthHoc(App);
